/**
 * The shape of the platform's background activity (GET /v1/activities and its stream): the
 * floating activity dock reads it. Mirrors apps/api/src/modules/activities/activity.types.ts.
 *
 * Every number is the owning module's SQL aggregate; the dock adds nothing
 * (done + failed + skipped + remaining = total). Text is never sent: the dock draws its sentences
 * from catalogue keys, and only what a person typed (a run's name, a Move folder) arrives as text.
 */

export type ActivityState =
  | 'queued'
  | 'running'
  | 'paused'
  | 'finished'
  | 'finished_with_failures'
  | 'cancelled'
  | 'failed'

export type ActivityActionKey = 'pause' | 'resume' | 'cancel' | 'retryFailed' | 'undo'

export interface ActivityCounts {
  total: number
  done: number
  failed: number
  skipped: number
  remaining: number
}

export interface ActivityReasonCount {
  code: string
  count: number
  /** null: the module does not record whether a retry can help. */
  retryable: boolean | null
}

export interface ActivityDto {
  id: string
  module: string
  kind: string
  refId: string
  parentId: string | null
  state: ActivityState
  unit: 'mailboxes' | 'copies'
  counts: ActivityCounts
  metrics: Record<string, number>
  failedByReason: ActivityReasonCount[]
  /** Typed text (a run's name): shown through <UserText>, never translated. */
  label: string | null
  detail: Record<string, string | number | null>
  startedAt: string | null
  finishedAt: string | null
  startedBy: { id: string; name: string | null } | null
  actions: ActivityActionKey[]
  href: string | null
}

export interface ActivitiesSnapshot {
  activities: ActivityDto[]
  /** Modules whose activity could not be read: "could not load" is not "nothing is running". */
  unavailable: string[]
  generatedAt: string
  version: string
}

/** Not over yet: running, queued or paused. */
export function isOpenState(state: ActivityState): boolean {
  return state === 'queued' || state === 'running' || state === 'paused'
}

/** Over, and a person should look: it failed in part or whole. */
export function needsAttention(state: ActivityState): boolean {
  return state === 'failed' || state === 'finished_with_failures'
}

/** Work that is going right now (the pill's spinner and the 2 s poll). */
export function isProgressing(state: ActivityState): boolean {
  return state === 'queued' || state === 'running'
}

/** How many items are over: the numerator of every bar and of "45 of 100". */
export function handled(c: ActivityCounts): number {
  return c.done + c.failed + c.skipped
}
